import { useEffect, useRef, useState } from "react";
function PetalField({
  count = 14,
  className = ""
}) {
  const [petals, setPetals] = useState([]);
  useEffect(() => {
    setPetals(
      Array.from({ length: count }, () => ({
        left: Math.random() * 100,
        size: 9 + Math.random() * 11,
        dur: 13 + Math.random() * 14,
        delay: Math.random() * 16,
        drift: (Math.random() - 0.5) * 9,
        hue: Math.random()
      }))
    );
  }, [count]);
  return <div
    aria-hidden="true"
    className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}
  >
      {petals.map((p, i) => <span
    key={i}
    className="css-petal"
    style={{
      left: `${p.left}%`,
      width: p.size,
      height: p.size * 1.25,
      animationDuration: `${p.dur}s`,
      animationDelay: `-${p.delay}s`,
      marginLeft: `${p.drift}px`,
      background: p.hue > 0.66 ? "radial-gradient(circle at 32% 28%, #ffe3e9, #f4a6c6 46%, #a2384c 100%)" : p.hue > 0.33 ? "radial-gradient(circle at 32% 28%, #ffd9e0, #c15fd6 48%, #6e1523 100%)" : "radial-gradient(circle at 32% 28%, #e0f2fe, #e9a8f2 46%, #9c7433 100%)"
    }}
  />)}
    </div>;
}
function LightRays({
  intensity = 2,
  reduced = false,
  className = ""
}) {
  const strength = 0.24 + intensity * 0.15;
  const rays = [
    { left: "46%", rotate: 12, width: "34vw", opacity: 0.075, delay: "0s" },
    { left: "54%", rotate: -9, width: "20vw", opacity: 0.055, delay: "-4s" },
    { left: "30%", rotate: 20, width: "13vw", opacity: 0.038, delay: "-7s" },
    { left: "70%", rotate: -17, width: "15vw", opacity: 0.045, delay: "-2.5s" }
  ];
  return <div aria-hidden="true" className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}>
      {
    /* warm bloom behind the centrepiece */
  }
      <div
    style={{
      position: "absolute",
      left: "50%",
      top: "36%",
      width: "min(125vw, 1040px)",
      height: "min(125vw, 1040px)",
      transform: "translate(-50%, -50%)",
      background: `radial-gradient(circle, rgba(214,190,255,${strength * 0.32}) 0%, rgba(244,166,198,${strength * 0.19}) 32%, rgba(76,29,149,${strength * 0.15}) 60%, transparent 78%)`,
      willChange: "opacity",
      animation: reduced ? void 0 : "lux-glow 11s ease-in-out infinite"
    }}
  />

      {
    /* volumetric shafts */
  }
      {rays.map((r, i) => <div
    key={i}
    style={{
      position: "absolute",
      left: r.left,
      top: "-24%",
      width: r.width,
      height: "150%",
      transform: `translateX(-50%) rotate(${r.rotate}deg)`,
      // The rotation lives on a CSS variable so `ray-breathe` can re-apply it
      // in its own transform — otherwise the animation's transform would
      // overwrite the inline rotate and the shafts would all stand upright.
      // @ts-expect-error -- custom property, valid CSS
      "--ray-rot": `${r.rotate}deg`,
      background: `radial-gradient(ellipse 50% 100% at 50% 0%, rgba(235,230,255,${r.opacity * strength * 1.6}) 0%, rgba(200,190,255,${r.opacity * strength * 0.8}) 38%, transparent 72%)`,
      willChange: "opacity",
      animation: reduced ? void 0 : `ray-breathe 13s ease-in-out infinite`,
      animationDelay: r.delay
    }}
  />)}

      {
    /* a soft floor of light so the bottom of the frame is not dead black */
  }
      <div
    style={{
      position: "absolute",
      left: "50%",
      bottom: "-28%",
      width: "min(140vw, 1200px)",
      height: "60vh",
      transform: "translateX(-50%)",
      background: `radial-gradient(ellipse at 50% 50%, rgba(120,70,200,${strength * 0.2}) 0%, transparent 66%)`
    }}
  />
      {
    /* The ray-breathe keyframes live here rather than in index.css because this
       component is the only consumer; keeping the animation next to the element
       it animates means the two cannot drift apart. */
  }
      <style>{`
        @keyframes ray-breathe {
          0%, 100% { opacity: .55; }
          50%      { opacity: 1; }
        }
      `}</style>
    </div>;
}
function FilmGrade({ reduced = false }) {
  return <div aria-hidden="true" className="pointer-events-none">
      <div className="film-grain" style={reduced ? { animation: "none", opacity: 0.1 } : void 0} />
      <div
    style={{
      position: "fixed",
      inset: 0,
      zIndex: 3,
      pointerEvents: "none",
      background: "radial-gradient(118% 88% at 50% 42%, transparent 38%, rgba(0,0,0,.40) 76%, rgba(0,0,0,.80) 100%)"
    }}
  />
    </div>;
}
function IntroVeil({
  siteTitle,
  reduced,
  onDone
}) {
  const [done, setDone] = useState(reduced);
  const firedRef = useRef(false);
  useEffect(() => {
    const finish = () => {
      setDone(true);
      if (!firedRef.current) {
        firedRef.current = true;
        onDone?.();
      }
    };
    let seen = false;
    try {
      seen = sessionStorage.getItem("mfb_intro") === "1";
    } catch {
    }
    if (reduced || seen) {
      finish();
      return;
    }
    const t = window.setTimeout(() => {
      try {
        sessionStorage.setItem("mfb_intro", "1");
      } catch {
      }
      finish();
    }, 1750);
    return () => window.clearTimeout(t);
  }, [reduced, onDone]);
  if (done && reduced) return null;
  return <div className="intro-veil" data-done={done} aria-hidden="true">
      <div className="text-center">
        <div className="intro-heart mx-auto mb-7 h-14 w-14">
          <svg viewBox="0 0 32 30" width="100%" height="100%" aria-hidden="true">
            <defs>
              <linearGradient id="introHeart" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#f5e9ff" />
                <stop offset="52%" stopColor="#f4a6c6" />
                <stop offset="100%" stopColor="#9b6fd6" />
              </linearGradient>
            </defs>
            <path
    d="M16 28.5C16 28.5 1.5 19.7 1.5 10.9 1.5 5.7 5.4 2 9.8 2c2.9 0 4.9 1.5 6.2 3.3C17.3 3.5 19.3 2 22.2 2c4.4 0 8.3 3.7 8.3 8.9 0 8.8-14.5 17.6-14.5 17.6z"
    fill="none"
    stroke="url(#introHeart)"
    strokeWidth="0.6"
  />
          </svg>
        </div>

        <div className="intro-mask">
          <span className="intro-word gold-text display-lg"><Emojiful text={siteTitle} /></span>
        </div>

        <div className="mt-5 flex justify-center">
          <div className="intro-rule" />
        </div>
      </div>
    </div>;
}
function Reveal({
  children,
  delay = 0,
  className = "",
  reduced = false
}) {
  const ref = useRef(null);
  const [seen, setSeen] = useState(reduced);
  useEffect(() => {
    if (reduced) return;
    const node = ref.current;
    if (!node) return;
    if (typeof IntersectionObserver === "undefined") {
      setSeen(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setSeen(true);
          io.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: "0px 0px -6% 0px" }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [reduced]);
  return <div
    ref={ref}
    className={className}
    style={{
      opacity: seen ? 1 : 0,
      transform: seen ? "none" : "translateY(30px) scale(0.994)",
      transition: reduced ? void 0 : `opacity 1.15s cubic-bezier(.16,1,.3,1) ${delay}ms, transform 1.15s cubic-bezier(.16,1,.3,1) ${delay}ms`
    }}
  >
      {children}
    </div>;
}
function Rule({ className = "" }) {
  return <div aria-hidden="true" className={`gold-rule ${className}`} />;
}
function useHeroScroll() {
  const progress = useRef(0);
  useEffect(() => {
    const onScroll = () => {
      const vh = window.innerHeight || 1;
      progress.current = Math.min(1.4, Math.max(0, window.scrollY / vh));
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return progress;
}
export {
  FilmGrade,
  IntroVeil,
  LightRays,
  PetalField,
  Reveal,
  Rule,
  useHeroScroll
};

const EMOJI = /(\p{Extended_Pictographic}(?:\uFE0F|\u200D\p{Extended_Pictographic})*\uFE0F?)/u;
export function Emojiful({ text }) {
  return String(text ?? "").split(EMOJI).map((part, i) => i % 2 ? <span key={i} style={{ color: "initial", WebkitTextFillColor: "initial" }}>{part}</span> : part);
}
